import TableContainer from "components/Common/TableContainer";
import en from "date-fns/locale/en-US";
import ko from "date-fns/locale/ko";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useTranslation } from "react-i18next";
import "react-toastify/dist/ReactToastify.css";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Input,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
} from "reactstrap";
import CountUp from "react-countup";
import CollapseFilter from "components/Common/CollapseFilter";
import LabelWrapper from "components/Common/LabelWrapper";
import CopyWrapper from "components/Common/CopyWrapper";
import avatar1 from "../../assets/images/users/ic_defaul_avatar2.png";
import { truncate } from "utils";
import { getChatGroupMessages } from "store/chatLog/thunk";
import { formatDateStringToKorea } from "helpers/format";

registerLocale("en", en);
registerLocale("ko", ko);

interface ModalUserLogProps {
  header?: string;
  item?: any;
  isOpen?: boolean;
  onClose?: () => void;
  setLinkImage?: any;
}

const DEFAULT_DATA_SEARCH = {
  nickname: "",
  email: "",
  uuid: "",
};

const ModalGroupMember = ({
  header,
  item,
  onClose,
  setLinkImage
}: ModalUserLogProps) => {
  const { t, i18n } = useTranslation();

  const [querySearch, setQuerySearch] = useState<any>(DEFAULT_DATA_SEARCH);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [listMember, setListMember] = useState<any>([]);

  const handleCloseConfirmClick = () => {
    onClose && onClose();
  };

  // Column
  const columns = useMemo(
    () => [
      {
        Header: t("No."),
        thWidth: 60,
        thClass: 'whitespace-nowrap',
        Cell: ({ ...props }) => {
          return (
            <div>
              {listMember?.length - props?.row?.index}
            </div>
          );
        },
      },
      {
        Header: t("Avatar"),
        accessor: "profile_image_url",
        thClass: "text-center whitespace-nowrap",
        filterable: true,
        sortable: false,
        thWidth: 80,
        Cell: (cell: any) => {
          
          return (
            <>
              <div className="w-100 text-center " style={{ minWidth: "60px" }}>
                <div
                  style={{ width: "45px" }}
                  className="m-auto cursor-pointer"
                  onClick={() => setLinkImage((prev: any) => cell?.value)}
                >
                  <img
                    src={cell?.value ?? avatar1}
                    className="rounded-circle avatar-sm"
                    alt="thumbnail"
                    onError={({ currentTarget }) => {
                      currentTarget.onerror = null; // prevents looping
                      currentTarget.src = avatar1;
                    }}
                  />
                </div>
              </div>
            </>
          )
        },
      },
      {
        Header: t("Nickname"),
        accessor: "nickname",
        filterable: true,
        sortable: false,
        thClass: "whitespace-nowrap",
        thWidth: 150,
        Cell: (cell: any) => {
          let nickname = cell?.value;

          return (
            nickname && (
              <div className="cursor-pointer" style={{minWidth: 120}}>
                <CopyWrapper contentCopy={nickname}>
                  {`${nickname}`}
                </CopyWrapper>
              </div>
            )
          );
        },
      },

      {
        Header: t("Email"),
        accessor: "email",
        filterable: true,
        sortable: false,
        thClass: "whitespace-nowrap",
        thWidth: 150,
        Cell: (cell: any) => {
          let email = cell?.value;

          return (
            email && (
              <div className="cursor-pointer" style={{minWidth: 120}}>
                <CopyWrapper contentCopy={email}>
                  {`${email}`}
                </CopyWrapper>
              </div>
            )
          );
        },
      },

      {
        Header: t("Joined Date"),
        accessor: "joinedAt",
        filterable: true,
        sortable: false,
        thWidth: 130,
        thClass: "text-end whitespace-nowrap",
        Cell: (cell: any) => {
          const date = formatDateStringToKorea(cell?.value);
          const arrDate = String(date || "").split(" ");

          return (
            <div className="text-end" style={{ minWidth: "100px" }}>
              <span>
                <span>{arrDate?.[0] || ""}</span> <br />{" "}
                <span className="text-secondary">{arrDate?.[1] || ""}</span>{" "}
              </span>
            </div>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [i18n?.language, listMember?.length]
  );

  useEffect(() => {
    const loadTableData = async () => {
      if(!item?._id) return;

      try {
        setIsLoading(true);
        const dataRes: any = await getChatGroupMessages(item?._id, {});
  
        if (dataRes?.data) {
          setListMember(dataRes?.data);
          setIsLoading(false);
        } else {
          setIsLoading(false);
        }

      } catch (error) {
        setIsLoading(false);
      }
    };

    if (item) {
      loadTableData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item]);

  // End::Delete
  useEffect(() => {
    document.title = `${t("User Logs")} - ${t("Send Push (Manual)")} | Baive`;
    document.body.classList.remove("vertical-sidebar-enable");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n?.language]);

  return (
    <React.Fragment>
      <Modal
        isOpen={!!item}
        centered={true}
        size="xl"
        className=""
        toggle={handleCloseConfirmClick}
        keyboard={true}
      >
        <ModalHeader toggle={handleCloseConfirmClick}>
          {
          header ?? (
            <>
            {`${t("List Member")} - `}
              <b>{item?.groupInfo?.name}</b>
            </>
          )} 
        </ModalHeader>
        <ModalBody className="">
          <div className="mt-3">
            <Container fluid>
              <Row>
                <Col lg={12}>
                  <Card
                    id="review-rating"
                    style={{ boxShadow: "0px 3px 4px 0px rgba(0, 0, 0, 0.03)" }}
                  >
                    <CardHeader className="border-0 d-flex">
                      <div className="col-sm">
                        <div>
                          <h5 className="card-title mb-0">
                            <span className="me-2">{t("Total")}:</span>
                            <CountUp
                              start={0}
                              end={listMember?.length || 0}
                              duration={1}
                              className="text-primary"
                            />
                          </h5>
                        </div>
                      </div>
                    </CardHeader>
                    
                    <CardBody className="pt-3">
                      <div>
                        <TableContainer
                          className="custom-header-css"
                          divClass="table-responsive table-card"
                          tableClass="align-middle table-bordered-dashed"
                          theadClass="table-light text-muted"
                          columns={columns}
                          data={listMember?.length ? listMember : []}
                          customPageSize={querySearch.limit}
                          customPageIndex={querySearch.page - 1}
                          totalRecords={listMember?.length}
                          manualSorting={true}
                          isLoading={isLoading}
                          defaultScrollBottom={(listMember?.list ?? [])?.length < 5 ? true : false}
                          isShowPagination={false}
                        />
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </Container>
          </div>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default ModalGroupMember;
